import { zodResolver } from "@hookform/resolvers/zod";
import {
  AccountData,
  CardProTemplateData,
  CardProWiringProviderInterface,
  useFormDataContext,
} from "@totalenergiescode/mobility-business-rev-form-data/components";
import { TFunction } from "i18next";
import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  getCreationTemplateFormSchema,
  GetCreationTemplateFormType,
} from "./types";

type UseCreationTemplateParams = {
  provider: CardProWiringProviderInterface;
  tFormData: TFunction<"formData">;
};

export function useCreationTemplate({
  provider,
  tFormData,
}: UseCreationTemplateParams) {
  const { providers: formDataProviders } = useFormDataContext();

  const [selectedAccount, setSelectedAccount] = useState<AccountDetails | null>(
    null,
  );
  const [accountError, setAccountError] = useState<string | null>(null);
  const [accountLoading, setAccountLoading] = useState<boolean>(false);
  const [divisionOptions, setDivisionOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [ledgerTypeOptions, setLedgerTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [creationTemplateLoading, setCreationTemplateLoading] =
    useState<boolean>(false);
  const [creationTemplateError, setCreationTemplateError] = useState<
    string | null
  >(null);
  const [creationTemplateData, setCreationTemplateData] =
    useState<CardProTemplateData | null>(null);

  const form = useForm<GetCreationTemplateFormType>({
    resolver: zodResolver(getCreationTemplateFormSchema),
    defaultValues: {
      accountId: "", //example : 87123456
      walletId: "",
      ledgerTypeId: "",
    },
  });

  const walletId = form.watch("walletId");
  const ledgerTypeId = form.watch("ledgerTypeId");

  function resetTemplateState() {
    setCreationTemplateError(null);
    setCreationTemplateData(null);
  }

  function resetLedgerSelection() {
    setLedgerTypeOptions([]);
    form.setValue("ledgerTypeId", "");
    resetTemplateState();
  }

  async function handleSearchAccount() {
    setAccountLoading(true);
    setDivisionOptions([]);
    resetLedgerSelection();
    setSelectedAccount(null);
    setAccountError(null);
    form.setValue("walletId", "");
    const accountId = form.getValues("accountId").trim();

    try {
      const account = await provider.accounts.search(accountId);
      setSelectedAccount(account);

      if (!account) {
        setAccountError(tFormData("formData.creationTemplate.accountNotFound"));

        return;
      }

      const divisions = await provider.wallets.search(account.accountId);
      const options = Array.from(divisions.entries()).map(
        ([walletId, divisionLabel]) => ({
          value: walletId,
          label: divisionLabel,
        }),
      );

      setDivisionOptions([
        {
          value: "",
          label: tFormData("formData.creationTemplate.divisionPlaceholder"),
        },
        ...options,
      ]);
      form.setValue("walletId", "");
    } catch (reason) {
      console.error("Account search failed: ", reason);
      setAccountError(
        tFormData("formData.creationTemplate.accountSearchError"),
      );
    } finally {
      setAccountLoading(false);
    }
  }

  async function handleSelectWallet() {
    const nextWalletId = form.getValues("walletId")?.trim();

    resetLedgerSelection();

    if (!selectedAccount || !nextWalletId) {
      return;
    }

    try {
      const ledgerTypes = await provider.ledgerTypes.search(nextWalletId);
      const options = Array.from(ledgerTypes.entries()).map(
        ([ledgerTypeId, ledgerTypeData]) => {
          const code = (ledgerTypeData as { code?: string })?.code;
          const label = ledgerTypeData.label || ledgerTypeId;
          const display = code ? `${code} — ${label}` : label;

          return {
            value: ledgerTypeId,
            label: display || ledgerTypeId,
          };
        },
      );

      setLedgerTypeOptions([
        {
          value: "",
          label: tFormData("formData.creationTemplate.ledgerTypePlaceholder"),
        },
        ...options,
      ]);
    } catch (reason) {
      console.error("Ledger types search failed: ", reason);
      setLedgerTypeOptions([]);
    }
  }

  function handleSelectLedgerType() {
    resetTemplateState();
  }

  async function handleFetchCreationTemplate() {
    if (!selectedAccount || !walletId || !ledgerTypeId) {
      return;
    }

    setCreationTemplateLoading(true);
    setCreationTemplateError(null);

    try {
      const template = await formDataProviders.creationTemplate.search(
        selectedAccount.accountId,
        ledgerTypeId,
        walletId,
      );
      setCreationTemplateData(template);
    } catch (reason) {
      console.error("Creation template fetch failed: ", reason);
      setCreationTemplateError(
        tFormData("formData.creationTemplate.messages.fetchError"),
      );
    } finally {
      setCreationTemplateLoading(false);
    }
  }

  const canFetchCreationTemplate =
    Boolean(selectedAccount && walletId && ledgerTypeId) &&
    !creationTemplateLoading;

  return {
    form,
    walletId,
    ledgerTypeId,
    selectedAccount,
    accountError,
    divisionOptions,
    ledgerTypeOptions,
    creationTemplateError,
    creationTemplateData,
    handleSearchAccount,
    handleSelectWallet,
    handleSelectLedgerType,
    handleFetchCreationTemplate,
    canFetchCreationTemplate,
    isAccountLoading: accountLoading,
    isCreationTemplateLoading: creationTemplateLoading,
  };
}

type AccountDetails = AccountData & { number?: string; name?: string };
